import React, { useState } from 'react';
import {
  Box,
  TextInput,
  Switch,
  Button,
  ArrowLeftIcon,
  Text,
  useToast,
} from '@razorpay/blade/components';
import { useForm } from 'forms';
import {
  FormHeading,
  StyledForm,
  FormFooterWrapper,
  FormFieldsWrapper,
  BorderedBox,
} from '../components/styles';
import {
  invalidateDraftEmployeeCache,
  statutoryValidationSchema,
  statutoryValidationSchemaWithoutEmail,
  updateInitialStatutoryFormData,
} from './utils';
import { initialComplianceData } from './data';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';
import { FloatingDiscardButton } from '../components/FloatingDiscardButton';
import { PFStatusSelectInput } from 'components/EmployeeDetails/StatutoryContributions/components/PFStatusSelectInput';
import { StatutoryFormFields } from './types';
import { ESICStatusSelectInput } from 'components/EmployeeDetails/StatutoryContributions/components/ESICStatusSelectInput';
import { portalStatus } from 'components/EmployeeDetails/StatutoryContributions/data';
import { LwfStateSelectInput } from 'components/EmployeeDetails/StatutoryContributions/components/LwfStateSelectInput';
import { PtStateSelectInput } from 'components/EmployeeDetails/StatutoryContributions/components/PtStateSelectInput';
import { PayrollUpdateDraftEmployeeComplianceStepInput } from 'api/gql-utils/employeeOnboardingGql/types';
import { useDraftEmployeeData } from './hooks/useDraftEmployeeData';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { useDraftEmployeeId } from './hooks/useDraftEmployeeId';
import { AppError } from 'utils/AppError';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import DuplicateChecksModal from 'components/EmployeeDetails/StatutoryContributions/components/DuplicateChecksModal';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { getDuplicateDetails } from '../utils';

interface StatutoryDetailsProps {
  setShowDiscardModal: (value: boolean) => void;
  setShowSaveDraftAndCloseModal: (value: boolean) => void;
}

export const StatutoryDetails: React.FC<StatutoryDetailsProps> = ({
  setShowDiscardModal,
  setShowSaveDraftAndCloseModal,
}) => {
  const [isDuplicateChecksModalOpen, setIsDuplicateChecksModalOpen] = useState(false);
  const { register, validate, formData, setIsSubmitting, isSubmitting, setFormData } =
    useForm<StatutoryFormFields>(initialComplianceData);

  const { isM3S1Enabled, isM3S3Enabled } = useIsFeatureDisabledForRearch();

  const toast = useToast();
  const history = useHistory();
  const { draftEmployeeId } = useDraftEmployeeId();
  const queryClient = useQueryClient();

  const { isLoading, isFetching, data } = useDraftEmployeeData({
    onSuccess: (data) => updateInitialStatutoryFormData(data, setFormData),
  });
  const isComplianceDetailsRequired = data?.basicDetails?.emailNotPresent ?? false;

  const { mutateAsync: updateDraftEmployeeComplianceStep, error } = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.updateDraftEmployeeComplianceStep,
    onSuccess: () => {
      invalidateDraftEmployeeCache(queryClient, draftEmployeeId);
    },
    onError: (error: typeof AppError) => {
      const duplicateDetails = getDuplicateDetails(error);
      if (duplicateDetails) {
        setIsDuplicateChecksModalOpen(true);
      }
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
      setIsSubmitting(false);
    },
  });

  const getPayload = (): PayrollUpdateDraftEmployeeComplianceStepInput => {
    return {
      isTds: formData.isTdsEnabled ?? false,
      isPf: formData.isPfEnabled ?? false,
      pfStatus: formData.isPfEnabled
        ? formData.pfStatus?.[0] || portalStatus.notRegistered
        : undefined,
      isEsi: formData.isEsicEnabled ?? false,
      esicStatus: formData.isEsicEnabled
        ? formData.esicStatus?.[0] || portalStatus.notRegistered
        : undefined,
      isPt: formData.isPtEnabled ?? false,
      locationPt: formData.ptState?.[0] ? Number(formData.ptState[0]) : 0,
      isLwf: formData.isLwfEnabled ?? false,
      locationLwf: formData.lwfLocation?.[0] ? Number(formData.lwfLocation[0]) : 0,
      pan: formData.isTdsEnabled ? formData.pan : undefined,
      pfUan:
        formData.isPfEnabled && formData.pfStatus?.[0] === portalStatus.registered
          ? formData.pfUan
          : undefined,
      esicIp:
        formData.isEsicEnabled && formData.esicStatus?.[0] === portalStatus.registered
          ? formData.esicIpNumber
          : undefined,
    };
  };

  const handleSubmit = async (e: React.FormEvent, onSuccess: () => void) => {
    e.preventDefault();

    const isFormValid = isComplianceDetailsRequired
      ? validate(statutoryValidationSchemaWithoutEmail)
      : validate(statutoryValidationSchema);

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);

    const res = await updateDraftEmployeeComplianceStep({
      draftEmployeeId,
      input: getPayload(),
    });

    if (res?.payrollUpdateDraftEmployeeCompliance?.data?.id) {
      onSuccess();
    }
  };

  const handleContinueSuccess = () => {
    setIsSubmitting(false);
    if (isM3S1Enabled) {
      history.push({
        pathname: routePaths.employeeOnboarding.employee.compensationBreakdown,
        search: `?draftId=${draftEmployeeId}`,
      });
    } else {
      history.push({
        pathname: routePaths.employeeOnboarding.employee.review,
        search: `?draftId=${draftEmployeeId}`,
      });
    }
  };

  const handleSaveAndCloseSuccess = () => {
    setIsSubmitting(false);
    setShowSaveDraftAndCloseModal(true);
  };

  const handleStepBack = () => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.compensation,
      search: `?draftId=${draftEmployeeId}`,
    });
  };

  const shouldDisableButtons = isLoading || isFetching || isSubmitting;

  const duplicateDetails = getDuplicateDetails(error);

  return (
    <>
      <FloatingDiscardButton
        isDisabled={shouldDisableButtons}
        handleDiscardModal={() => setShowDiscardModal(true)}
      />
      <StyledForm onSubmit={(e) => handleSubmit(e, handleContinueSuccess)}>
        <FormFieldsWrapper shouldShowSpinner={isLoading || isFetching}>
          <FormHeading
            title="Compliance Details"
            description="Provide compliance details to ensure statutory filings and contributions"
          />
          <BorderedBox>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text variant="body" size="medium" weight="regular">
                Tax Deducted at Source
              </Text>
              <Switch
                isChecked={!!formData.isTdsEnabled}
                {...register('isTdsEnabled', 'checkbox')}
                size="medium"
                accessibilityLabel="Enable Tax Deducted at Source"
              />
            </Box>
            {formData.isTdsEnabled && (
              <TextInput
                {...register('pan')}
                label="PAN"
                placeholder="ABCDE1234F"
                helpText="Employee's PAN number is required to calculate their income tax."
                size="medium"
              />
            )}
          </BorderedBox>

          <BorderedBox>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text variant="body" size="medium" weight="regular">
                Provident Fund
              </Text>
              <Switch
                {...register('isPfEnabled', 'checkbox')}
                size="medium"
                accessibilityLabel="Enable Provident Fund"
                isChecked={!!formData.isPfEnabled}
              />
            </Box>
            {formData.isPfEnabled && (
              <PFStatusSelectInput register={register} formData={formData} />
            )}
            {formData.isPfEnabled && formData.pfStatus?.[0] === portalStatus.registered && (
              <TextInput
                {...register('pfUan')}
                label="PF UAN"
                placeholder="Eg: 100123456789"
                size="medium"
                necessityIndicator={isComplianceDetailsRequired ? 'required' : 'none'}
                isRequired={isComplianceDetailsRequired}
              />
            )}
          </BorderedBox>

          <BorderedBox>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text variant="body" size="medium" weight="regular">
                Employees State Insurance Corporation
              </Text>
              <Switch
                {...register('isEsicEnabled', 'checkbox')}
                size="medium"
                accessibilityLabel="Enable Employees State Insurance Corporation"
                isChecked={!!formData.isEsicEnabled}
              />
            </Box>
            {formData.isEsicEnabled && (
              <ESICStatusSelectInput register={register} formData={formData} />
            )}
            {formData.isEsicEnabled && formData.esicStatus?.[0] === portalStatus.registered && (
              <TextInput
                {...register('esicIpNumber')}
                label="ESIC IP Number"
                placeholder="Eg: 100123456789"
                size="medium"
                necessityIndicator={isComplianceDetailsRequired ? 'required' : 'none'}
                isRequired={isComplianceDetailsRequired}
              />
            )}
          </BorderedBox>

          <BorderedBox>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text variant="body" size="medium" weight="regular">
                Professional Tax
              </Text>
              <Switch
                {...register('isPtEnabled', 'checkbox')}
                size="medium"
                accessibilityLabel="Enable Professional Tax"
                isChecked={!!formData.isPtEnabled}
              />
            </Box>
            {formData.isPtEnabled && (
              <PtStateSelectInput registerFormData={register} formData={formData} />
            )}
          </BorderedBox>

          <BorderedBox>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text variant="body" size="medium" weight="regular">
                Labour Welfare Fund
              </Text>
              <Switch
                {...register('isLwfEnabled', 'checkbox')}
                size="medium"
                accessibilityLabel="Enable Labour Welfare Fund"
                isChecked={!!formData.isLwfEnabled}
              />
            </Box>
            {formData.isLwfEnabled && (
              <LwfStateSelectInput registerFormData={register} formData={formData} />
            )}
          </BorderedBox>
        </FormFieldsWrapper>

        <FormFooterWrapper>
          <Button
            variant="tertiary"
            size="medium"
            icon={ArrowLeftIcon}
            onClick={handleStepBack}
            isDisabled={shouldDisableButtons}>
            Back
          </Button>
          <Box display="flex" gap="spacing.3">
            <Button
              variant="secondary"
              type="button"
              size="medium"
              onClick={(e) => handleSubmit(e, handleSaveAndCloseSuccess)}
              isLoading={shouldDisableButtons}>
              Save and Close
            </Button>
            <Button type="submit" variant="primary" size="medium" isLoading={shouldDisableButtons}>
              Continue
            </Button>
          </Box>
        </FormFooterWrapper>
      </StyledForm>

      <DuplicateChecksModal
        isOpen={isDuplicateChecksModalOpen}
        onDismiss={() => setIsDuplicateChecksModalOpen(false)}
        details={duplicateDetails}
      />
    </>
  );
};
